import useSWR, { type Fetcher } from 'swr';

import {
  callbacksList,
  callbacksSummaryRetrieve,
} from '~/data/generated/endpoints/callbacks/callbacks';
import type { CallbacksListParams } from '~/data/generated/model';
import { parseApiResponse } from '~/data/parseApiResponse';
import {
  type CallbackSummaryResponse,
  callbackSummarySchema,
  type CallbackTicket,
  callbackTicketListSchema,
} from '~/data/schemas/callbacks';

export type CallbackScopeTarget = { property: string } | { car: string } | { contract: string };

export type CallbackScope = CallbackScopeTarget & { start_date?: string; end_date?: string };

type SummaryKey = readonly ['callback-summary', CallbacksListParams];

const summaryFetcher: Fetcher<CallbackSummaryResponse, SummaryKey> = async ([, params]) => {
  const { data } = await callbacksSummaryRetrieve(params);
  return parseApiResponse(callbackSummarySchema, data, 'CallbackSummary');
};

export function useCallbackSummary(scope: CallbackScope | null) {
  const key: SummaryKey | null = scope ? ['callback-summary', scope] : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<
    CallbackSummaryResponse,
    Error,
    SummaryKey | null
  >(key, summaryFetcher, {
    // The window is part of the SWR key, so without this the table empties between
    // windows, which reads as the data having gone away.
    keepPreviousData: true,
  });

  return {
    summaries: data?.cars ?? [],
    window: data?.window ?? null,
    isLoading,
    isValidating,
    error,
    refresh: mutate,
  };
}

type TicketsKey = readonly ['callback-tickets', CallbacksListParams];

const ticketsFetcher: Fetcher<CallbackTicket[], TicketsKey> = async ([, params]) => {
  const { data } = await callbacksList(params);
  return parseApiResponse(callbackTicketListSchema, data, 'CallbackTickets');
};

export function useCallbackTickets(scope: CallbackScope | null) {
  const key: TicketsKey | null = scope ? ['callback-tickets', scope] : null;

  const { data, error, isLoading } = useSWR<CallbackTicket[], Error, TicketsKey | null>(
    key,
    ticketsFetcher,
  );

  return { tickets: data ?? [], isLoading, error };
}
